import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Buttons/Button';
import { WebPushPermissionStatus } from 'src/view/hooks/useWebPushRegistration';

const DISMISSED_STORAGE_KEY = 'mundu-web-push-permission-dismissed';

type Props = {
    isEligible: boolean;
    isRegistering: boolean;
    permissionStatus: WebPushPermissionStatus;
    requestWebPushPermission: () => Promise<void>;
};

export const WebPushPermissionPrompt = ({
    isEligible,
    isRegistering,
    permissionStatus,
    requestWebPushPermission,
}: Props) => {
    const { t } = useTranslation();
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        setDismissed(sessionStorage.getItem(DISMISSED_STORAGE_KEY) === 'true');
    }, []);

    const isPermissionPromptAvailable = permissionStatus === 'default';
    const isPermissionBlocked = permissionStatus === 'denied';

    if (
        !isEligible ||
        dismissed ||
        (!isPermissionPromptAvailable && !isPermissionBlocked)
    ) {
        return null;
    }

    const dismiss = () => {
        sessionStorage.setItem(DISMISSED_STORAGE_KEY, 'true');
        setDismissed(true);
    };

    return (
        <div className="fixed bottom-4 left-4 right-4 z-50 mx-auto flex max-w-3xl flex-col gap-3 rounded-lg border border-woodyBrown bg-vanillaWhite p-4 text-woodyBrown shadow-lg md:left-auto md:right-6 md:w-[420px]">
            <div>
                <p className="text-base font-semibold">
                    {t(
                        isPermissionBlocked
                            ? 'webPushPermission.blockedTitle'
                            : 'webPushPermission.title',
                    )}
                </p>
                <p className="mt-1 text-sm">
                    {t(
                        isPermissionBlocked
                            ? 'webPushPermission.blockedDescription'
                            : 'webPushPermission.description',
                    )}
                </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                {isPermissionPromptAvailable && (
                    <Button
                        title={
                            isRegistering
                                ? t('webPushPermission.enabling')
                                : t('webPushPermission.enable')
                        }
                        active
                        disabled={isRegistering}
                        onClick={() => void requestWebPushPermission()}
                    />
                )}
                <Button
                    title={t('webPushPermission.notNow')}
                    onClick={dismiss}
                />
            </div>
        </div>
    );
};
